import {
  type ReactElement,
  type ReactNode,
  useContext,
  useCallback,
  useState,
  isValidElement,
  cloneElement,
} from 'react'

import { type DrawerOpenOptions } from '../DrawerOpenOptions'
import { DrawersContext } from '../DrawersProvider'

type UseDrawerProps<T> = {
  drawerComponent?: ReactNode | ((props?: T) => ReactNode)
  initialDrawerOptions?: DrawerOpenOptions
}

export function useDrawer<T = unknown>(
  drawerInput?: ReactNode | UseDrawerProps<T>,
  options?: DrawerOpenOptions
) {
  const { openDrawer: openContextDrawer, closeDrawer: closeContextDrawer } =
    useContext(DrawersContext)

  // If drawerInput is not a React component, use its properties;
  // otherwise, use drawerInput as the component and options as initialDrawerOptions.
  const { drawerComponent, initialDrawerOptions } =
    typeof drawerInput === 'object' && !isValidElement(drawerInput)
      ? (drawerInput as UseDrawerProps<T>)
      : {
          drawerComponent: drawerInput as ReactNode,
          initialDrawerOptions: options,
        }

  const [_, setDrawerComponentProps] = useState<T | undefined>(undefined)

  // showDrawer opens the drawer in two ways:
  // If propsOrComponent is a valid React element,opens it with openContextDrawer.
  // Otherwise, it treats it as props, renders the drawer with the passed props,
  // then opens it using override options or initialDrawerOptions.
  const openDrawer = useCallback(
    (
      propsOrComponent?: T | ReactNode,
      overrideDrawerOptions?: DrawerOpenOptions
    ) => {
      if (
        isValidElement(propsOrComponent) ||
        typeof propsOrComponent === 'function'
      ) {
        openContextDrawer(propsOrComponent as ReactNode, overrideDrawerOptions)
        return
      }

      const finalProps = propsOrComponent as T | undefined
      setDrawerComponentProps(finalProps)

      const renderedDrawer =
        typeof drawerComponent === 'function' && drawerComponent !== null
          ? drawerComponent(finalProps)
          : cloneElement(drawerComponent as ReactElement, {
              ...finalProps,
            })

      openContextDrawer(
        renderedDrawer,
        overrideDrawerOptions || initialDrawerOptions
      )
    },
    [drawerComponent, initialDrawerOptions, openContextDrawer]
  )

  const closeDrawer = () => closeContextDrawer()

  return { openDrawer, closeDrawer }
}
